import { ComponentType, FC } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarLayoutNavItem, { SidebarLayoutNavRoot } from "@/components/atoms/SidebarLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCompanyById } from "@/services/Company";
import { useIsWhiteLabel } from "@/services/WhiteLabel";

export interface SettingsNavigationItem {
  url: string;
  translationsKey: string;
  icon: string;
}

const adminNavigation: SettingsNavigationItem[] = [
  {
    url: "/settings/account",
    translationsKey: "settings:navigation.myProfile",
    icon: "user",
  },
  {
    url: "/settings/company",
    translationsKey: "settings:navigation.company",
    icon: "companyXl",
  },
  {
    url: "/settings/departments",
    translationsKey: "settings:navigation.departments",
    icon: "companyXl",
  },
  {
    url: "/settings/logistics",
    translationsKey: "settings:navigation.logistics",
    icon: "logstics",
  },
  {
    url: "/settings/transport-devices",
    translationsKey: "settings:navigation.transportDevices",
    icon: "transport",
  },
  {
    url: "/settings/economy",
    translationsKey: "settings:navigation.economy",
    icon: "economyXl",
  },
  {
    url: "/settings/products",
    translationsKey: "settings:navigation.products",
    icon: "products",
  },
  {
    url: "/settings/services",
    translationsKey: "settings:navigation.services",
    icon: "hands-providing",
  },
  {
    url: "/settings/users",
    translationsKey: "settings:navigation.users",
    icon: "users",
  },
  {
    url: "/settings/customers",
    translationsKey: "settings:navigation.partners",
    icon: "partners",
  },
  {
    url: "/settings/templates",
    translationsKey: "settings:navigation.templates",
    icon: "contract-dark",
  },
  {
    url: "/settings/contract-archive",
    translationsKey: "settings:navigation.contractArchive",
    icon: "template",
  },
  {
    url: "/settings/deviation",
    translationsKey: "settings:navigation.deviation",
    icon: "tabler-icon-arrow-loop-right",
  },
  {
    url: "/settings/documents",
    translationsKey: "settings:navigation.documents",
    icon: "document-timer",
  },
  {
    url: "/settings/cameras",
    translationsKey: "settings:navigation.cameras",
    icon: "camera",
  },
  {
    url: "/settings/branding",
    translationsKey: "settings:navigation.branding",
    icon: "tabler-icon-palette",
  },
  /*
  disabled untill reviver implementing open market
  {
    url: "/settings/network",
    translationsKey: "settings:navigation.partners",
    icon: "partners",
  },
  disabled untill reviver implementing newsletter
  {
    url: "/settings/newsletter",
    translationsKey: "settings:advertisement",
    icon: "message",
  },
*/
];

const userNavigation: SettingsNavigationItem[] = [
  {
    url: "/settings/profile",
    translationsKey: "settings:navigation.myProfile",
    icon: "user",
  },
  {
    url: "/settings/services",
    translationsKey: "settings:navigation.services",
    icon: "services",
  },
  {
    url: "/settings/products",
    translationsKey: "settings:companySpecificProduct",
    icon: "template",
  },
  {
    url: "/settings/contract-archive",
    translationsKey: "settings:navigation.contractArchive",
    icon: "template",
  },
];

type PreloadComponents = Record<string, () => Promise<{ default: ComponentType<any> }>>;
interface Props {
  preloadComponents?: PreloadComponents;
}
const SettingsNavigation: FC<Props> = ({ preloadComponents }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentCompany, isAdminUser, isUserRole, isCurrentCompanyAdmin } = useAuth();
  const { data: company } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });
  const { isWhiteLabel } = useIsWhiteLabel();

  const getPreloadHandler = (url: string) => {
    if (!preloadComponents) return undefined;

    const pageName = url.split("/").pop();
    return preloadComponents[pageName || ""];
  };

  // Branding configures the white-label tenant, so it is only meaningful inside a
  // white-label portal — everything on that page keys off the tenant id.
  const isVisibleAdminItem = (item: SettingsNavigationItem) =>
    item.url !== "/settings/branding" || (isCurrentCompanyAdmin && isWhiteLabel);

  if (isAdminUser(currentCompany)) {
    const nav = [
      ...adminNavigation,
      ...(company?.isOrderPlanningEnabled
        ? [
            {
              url: "/settings/reception",
              translationsKey: "settings:navigation.reception",
              icon: "calendar",
            },
          ]
        : []),
    ].filter(isVisibleAdminItem);

    return (
      <SidebarLayoutNavRoot>
        {nav.map((item: any, i) => (
          <SidebarLayoutNavItem
            href={item.url}
            text={t(item.translationsKey)}
            isActive={location.pathname === item.url}
            icon={item.icon}
            key={i}
            onMouseEnter={() => getPreloadHandler(item.url)}
            onFocus={() => getPreloadHandler(item.url)}
          />
        ))}
      </SidebarLayoutNavRoot>
    );
  } else if (isUserRole(currentCompany)) {
    return (
      <SidebarLayoutNavRoot>
        {userNavigation.map((item: any, i) => (
          <SidebarLayoutNavItem
            href={item.url}
            text={t(item.translationsKey)}
            isActive={location.pathname === item.url}
            icon={item.icon}
            key={i}
            onMouseEnter={() => getPreloadHandler(item.url)}
            onFocus={() => getPreloadHandler(item.url)}
          />
        ))}
      </SidebarLayoutNavRoot>
    );
  } else {
    return (
      <SidebarLayoutNavItem
        href={"/settings/profile"}
        text={t("settings:navigation.myProfile")}
        isActive={true}
        icon={"user"}
        onMouseEnter={() => getPreloadHandler("/settings/profile")}
        onFocus={() => getPreloadHandler("/settings/profile")}
      />
    );
  }
};

export default SettingsNavigation;
